/* eslint-disable no-nested-ternary */
// React and core dependencies
import React, { useRef, useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";

// Date utilities
import { isSameDay } from "date-fns";
import { dateFormatWithDash } from "components/helper";

// Material UI components
import Grid from "@mui/material/Grid2";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

// Custom components
import DynamicDateRange from "components/DynamicDateRange";
import MaterialTablePicker from "components/MaterialTablePicker";
import columnsMap from "components/MaterialTablePicker/TableColumns";

// Redux actions and types
import {
  getLocationRequests,
  markAsOrdered,
  setLoading,
  updateLocationRequest,
} from "store/actions/requestActions";
import { CSV_SUCCESS, CSV_FAIL } from "store/actions/Types";

// Utility functions
import { exportLocationRequestsCSV } from "utils/LocationRequestsCsvUtils";

const LocationRequests = ({ locationId = null, locationIds = null }) => {
  const tableRef = useRef(null);
  const dispatch = useDispatch();

  // States
  const [dateRange, setDateRange] = useState([
    {
      startDate: new Date(new Date().setMonth(new Date().getMonth() - 1)),
      endDate: new Date(),
    },
  ]);
  const [hideDaterange, setHideDaterange] = useState(true);
  const [filterApplied, setFilterApplied] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  // Selectors
  const requests = useSelector((state) => state.requests.requests);
  const loading = useSelector((state) => state.requests.loading);
  const added = useSelector((state) => state.requests.added);
  const user = useSelector((state) => state.users.user);

  const requestParams = useMemo(() => {
    if (Array.isArray(locationIds)) {
      return { type: "multi", ids: locationIds };
    }
    if (locationId) {
      return { type: "single", id: locationId };
    }
    return { type: "none" };
  }, [locationId, locationIds]);

  const fetchRequests = useCallback(
    (startDate = null, endDate = null) => {
      if (requestParams.type === "none") return;

      dispatch(setLoading());
      if (requestParams.type === "multi") {
        dispatch(getLocationRequests(null, startDate, endDate, requestParams.ids));
      } else {
        dispatch(getLocationRequests(requestParams.id, startDate, endDate));
      }
    },
    [dispatch, requestParams]
  );

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  useEffect(() => {
    setSearchValue("");
  }, [locationId]);

  useEffect(() => {
    if (added) {
      fetchRequests();
    }
  }, [added, fetchRequests]);

  const handleDateChange = (date) => {
    if (date?.selection?.startDate && isSameDay(date.selection.startDate, date.selection.endDate)) {
      const newStartDate = date.selection.startDate;
      const newEndDate = dateRange[0].endDate;
      setDateRange([{ startDate: newStartDate, endDate: newEndDate }]);
    } else {
      const newStartDate = date?.range1?.startDate
        ? date?.range1.startDate
        : date?.selection?.startDate;
      const newEndDate = date?.range1?.endDate ? date?.range1.endDate : date?.selection?.endDate;
      setDateRange([{ startDate: newStartDate, endDate: newEndDate }]);
    }
  };

  const handleApplyFilter = () => {
    setFilterApplied(true);
    setHideDaterange(true);

    const startDateStr = dateFormatWithDash(dateRange[0].startDate, false);
    const endDateStr = dateFormatWithDash(dateRange[0].endDate, false);

    fetchRequests(startDateStr, endDateStr);
  };

  const handleResetFilter = () => {
    setFilterApplied(false);
    setDateRange([
      {
        startDate: new Date(new Date().setMonth(new Date().getMonth() - 1)),
        endDate: new Date(),
      },
    ]);

    fetchRequests();
  };

  const handleLocalSearchChange = useCallback((searchText) => {
    setSearchValue(searchText);
  }, []);

  const handleMarkAsOrdered = (event, rowData) => {
    dispatch(markAsOrdered(rowData.orderId));
  };

  const actionsChildFn = (rowData) => {
    let actionsChildRole = {};
    if (rowData.deliveryMethods.includes("noknok") && rowData.type === "Order") {
      const isOrdered = rowData.status;

      // Set action properties based on order status
      actionsChildRole = {
        icon: isOrdered === "succeeded" ? CheckCircleIcon : CheckCircleOutlineIcon,
        tooltip: isOrdered === "pending" ? "Mark as Ordered" : "",

        iconProps: {
          style: {
            marginLeft: "10px",
            fontSize: "24px",
            color: isOrdered === "succeeded" ? "green" : "black",
            cursor: isOrdered === "succeeded" ? "default" : "pointer",
          },
        },

        onClick: (event) => {
          if (isOrdered === "pending") {
            handleMarkAsOrdered(event, rowData);
          }
        },
      };
    }
    return actionsChildRole;
  };

  const handleExportCsv = () => {
    exportLocationRequestsCSV(requests, dispatch, { CSV_SUCCESS, CSV_FAIL });
  };

  const handleRowUpdate = (newData) => {
    const requestId = newData.orderId || newData._id;
    // Use the first location from locationIds or fall back to locationId
    const targetLocationId = locationIds && locationIds.length > 0 ? locationIds[0] : locationId;
    dispatch(
      updateLocationRequest(targetLocationId, {
        status: newData.status,
        requestId,
        type: newData.type,
      })
    );
  };

  return (
    <MDBox>
      <Grid container spacing={3}>
        <Grid size={{ xs: 6 }}>
          <DynamicDateRange
            defaultValue={dateRange}
            selectedOption={dateRange}
            handleDateChange={handleDateChange}
            hideDaterange={hideDaterange}
            hideDateRangeFn={setHideDaterange}
            isTodayIncluded
          />
          <MDBox display={filterApplied ? "block" : "none"} style={{ fontSize: "small" }}>
            Selected Date Range:{" "}
            {dateRange[0]?.startDate && !Number.isNaN(dateRange[0].startDate.getTime())
              ? dateRange[0].startDate.toDateString()
              : "Invalid date"}{" "}
            to{" "}
            {dateRange[0]?.endDate && !Number.isNaN(dateRange[0].endDate.getTime())
              ? dateRange[0].endDate.toDateString()
              : "Invalid date"}
          </MDBox>
        </Grid>
        <Grid size={{ xs: 6 }}>
          <MDBox sx={{ display: "flex", justifyContent: "flex-end" }}>
            <MDButton
              color="dark"
              variant="gradient"
              style={{
                marginTop: "30px",
                marginRight: "20px",
                width: "150px",
                height: "25px",
                color: "white",
              }}
              onClick={handleApplyFilter}
            >
              Apply Filter
            </MDButton>
            <MDButton
              color="dark"
              variant="gradient"
              style={{
                marginTop: "30px",
                marginRight: "0px",
                width: "150px",
                height: "25px",
                color: "white",
              }}
              onClick={handleResetFilter}
            >
              Reset Filters
            </MDButton>
          </MDBox>
        </Grid>
      </Grid>

      <MDBox mt={3}>
        <MaterialTablePicker
          screen="LocationRequests"
          title=""
          isSearchable
          columns={
            user.role === "admin"
              ? [
                  columnsMap.requestName,
                  columnsMap.deliveryMethods,
                  columnsMap.requestLastName,
                  columnsMap.requestDateAsync,
                  columnsMap.requestStatus,
                ]
              : user.role === "partner" && !!user?.partnerPower
              ? [
                  columnsMap.requestId,
                  columnsMap.requestStatus,
                  columnsMap.requestName,
                  columnsMap.locationForPartnerPower,
                  columnsMap.requestCategory,
                  columnsMap.deliveryMethods,
                  columnsMap.roomNumber,
                  columnsMap.requestLastName,
                  columnsMap.requestDateAsync,
                  columnsMap.assignee,
                ]
              : [
                  columnsMap.requestId,
                  columnsMap.requestStatus,
                  columnsMap.requestName,
                  columnsMap.requestCategory,
                  columnsMap.deliveryMethods,
                  columnsMap.roomNumber,
                  columnsMap.requestLastName,
                  columnsMap.requestDateAsync,
                  columnsMap.assignee,
                ]
          }
          tableRef={tableRef}
          data={requests}
          loading={loading}
          searchField={searchValue}
          filteringOptions={false}
          isExportable={user.role === "partner"}
          isMenuDownload={user.role === "partner"}
          actionsName="requestText"
          isRowClickable={false}
          onSearchChange={handleLocalSearchChange}
          searchRequestText={searchValue}
          setSearchRequestText={setSearchValue}
          actionsChild={actionsChildFn}
          actionsRole
          editableUpdate={handleRowUpdate}
          updateRoleAction={user.role !== "partner"}
          showAddButton={false}
          getCsv={handleExportCsv}
        />
      </MDBox>
    </MDBox>
  );
};

LocationRequests.propTypes = {
  locationId: PropTypes.string,
  locationIds: PropTypes.arrayOf(PropTypes.string),
};

export default LocationRequests;
